import type { GauntletControls } from '../shared/gauntletStep';
import type { GameId } from '../shared/games';

import { CryptogramPlay } from '../games/cryptogram/CryptogramGame';
import { HashtagPlay } from '../games/hashtag/HashtagGame';
import { CodeWordPlay } from '../games/codeWord/CodeWordGame';
import { normalizePuzzle as normalizeCodeWord } from '../games/codeWord/types';
import CipherPlay from '../games/cipher/CipherGame';
import BlackboxPlay from '../games/blackbox/BlackboxGame';
import PivotPlay from '../games/pivot/PivotGame';
import { normalizePuzzle as normalizeCipher } from '../games/cipher/types';
import { normalizePuzzle as normalizeBlackbox } from '../games/blackbox/types';
import { normalizePuzzle as normalizePivot } from '../games/pivot/types';

/**
 * Renders one gauntlet step. Every game in the quest pool implements the same
 * contract, so the host stays agnostic about which puzzle it's showing.
 *
 * Returns null when a payload can't be parsed — the host treats that as a
 * skippable step rather than crashing the run.
 */
export default function GauntletStep({
  gameId,
  puzzle,
  dateKey,
  controls,
}: {
  gameId: GameId;
  puzzle: Record<string, unknown>;
  dateKey: string;
  controls: GauntletControls;
}) {
  switch (gameId) {
    case 'cryptogram': {
      const level = puzzle as { text?: string; author?: string };
      if (!level.text) return null;
      return (
        <CryptogramPlay
          level={{ text: level.text, author: level.author ?? 'Unknown' }}
          dateKey={dateKey}
          isToday
          gauntlet={controls}
        />
      );
    }
    case 'hashtag': {
      const level = puzzle as { words?: string[]; initialLetters?: unknown };
      if (!level.words?.length) return null;
      return (
        <HashtagPlay
          level={puzzle as never}
          dateKey={dateKey}
          isToday
          gauntlet={controls}
        />
      );
    }
    case 'codeWord': {
      const normalized = normalizeCodeWord(puzzle as never);
      if (!normalized) return null;
      return (
        <CodeWordPlay
          puzzle={normalized}
          dateKey={dateKey}
          isToday
          gauntlet={controls}
        />
      );
    }
    case 'cipher': {
      const normalized = normalizeCipher(puzzle as never);
      if (!normalized) return null;
      return <CipherPlay puzzle={normalized} gauntlet={controls} />;
    }
    case 'blackbox': {
      const normalized = normalizeBlackbox(puzzle as never);
      if (!normalized) return null;
      return <BlackboxPlay puzzle={normalized} gauntlet={controls} />;
    }
    case 'pivot': {
      const normalized = normalizePivot(puzzle as never);
      if (!normalized) return null;
      return <PivotPlay puzzle={normalized} gauntlet={controls} />;
    }
    default:
      return null;
  }
}
